"use client";

import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, X, Check, Search } from "lucide-react";
import pettyCashService from "../apis/pettycash";
import { PETTY_CASH_DEPARTMENTS } from "../constants/pettyCashDepartments";

const syncLoggedInFullName = (fullName) => {
  if (!fullName?.trim()) return;
  const name = fullName.trim();
  localStorage.setItem("username", name);
  try {
    const profileRaw = localStorage.getItem("profile");
    const profile = profileRaw ? JSON.parse(profileRaw) : {};
    localStorage.setItem("profile", JSON.stringify({ ...profile, name }));
  } catch {
    localStorage.setItem("profile", JSON.stringify({ name }));
  }
  try {
    const userRaw = localStorage.getItem("user");
    if (userRaw) {
      const user = JSON.parse(userRaw);
      localStorage.setItem("user", JSON.stringify({ ...user, name }));
    }
  } catch {
    // ignore
  }
};

export const PettyCashAddModal = ({ isOpen, onClose, onAddTransaction, currentBalance }) => {
  const [formData, setFormData] = useState({
    transactionType: "withdraw",
    amount: "",
    name: "",
    department: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      setFormData({
        transactionType: "withdraw",
        amount: "",
        name: "",
        department: "",
        notes: "",
      });
      setError(null);
      setSearchTerm("");
      setDropdownOpen(null);
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    const res = await pettyCashService.getEmployees();
    if (res?.success) {
      const list = Array.isArray(res.data) ? res.data : [];
      // Backend puts logged-in user's full profile name first
      if (list[0]) {
        syncLoggedInFullName(list[0]);
      }
      setEmployees(list);
      setFilteredEmployees(list);
    } else {
      toast.error(res?.message || "Failed to load employees");
    }
  };

  useEffect(() => {
    setFilteredEmployees(
      employees.filter((emp) =>
        emp.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, employees]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTransactionTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      transactionType: value,
      name: value === "withdraw" ? prev.name : "",
    }));
  };

  const handleSubmit = async () => {
    const isNameRequired = formData.transactionType === "withdraw";

    if (
      !formData.amount ||
      isNaN(parseFloat(formData.amount)) ||
      parseFloat(formData.amount) <= 0
    ) {
      setError("Please enter a valid amount");
      return;
    }

    if (isNameRequired && !formData.name.trim()) {
      setError("Please enter the name of the person receiving cash");
      return;
    }

    if (!formData.department) {
      setError("Please select a department");
      return;
    }

    if (!formData.notes.trim()) {
      setError("Please provide transaction details");
      return;
    }

    const amount = parseFloat(formData.amount);

    if (formData.transactionType === "withdraw" && amount > currentBalance) {
      setError("Insufficient funds for this withdrawal");
      return;
    }

    setSubmitting(true);

    try {
      const success = await onAddTransaction({
        ...formData,
        amount,
        name: formData.transactionType === "withdraw" ? formData.name.trim() : "",
        department: formData.department,
        notes: formData.notes.trim(),
        receipts: [],
      });

      if (success) {
        handleClose();
        toast.success("Transaction added successfully!");
      }
    } catch (error) {
      console.error("Transaction submission error:", error);
      setError("Failed to add transaction. Please try again.");
      toast.error("Failed to add transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      transactionType: "withdraw",
      amount: "",
      name: "",
      department: "",
      notes: "",
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const isNameRequired = formData.transactionType === "withdraw";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Petty Cash Transaction</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Type
            </label>
            <Select
              name="transactionType"
              value={formData.transactionType}
              onValueChange={handleTransactionTypeChange}
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="withdraw">Withdrawal</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Amount
            </label>
            <Input
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              className="w-full h-10"
              min="0.01"
              step="0.01"
            />
          </div>

          {isNameRequired && (
            <div className="mb-4">
              <label className="text-sm font-medium">Cash Given To</label>
              <div className="relative" ref={dropdownRef}>
                <div
                  className="border rounded-md p-2 flex justify-between items-center cursor-pointer"
                  onClick={() =>
                    setDropdownOpen(
                      dropdownOpen === "employee" ? null : "employee"
                    )
                  }
                >
                  {formData.name ? (
                    <div className="flex items-center gap-2">
                      <span>{formData.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData((prev) => ({ ...prev, name: "" }));
                        }}
                      >
                        <X size={16} className="text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400">Select employee</span>
                  )}
                  <svg
                    className={`w-4 h-4 ml-2 ${
                      dropdownOpen === "employee" ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {dropdownOpen === "employee" && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="sticky top-0 bg-white p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search employees..."
                          className="pl-8"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="py-1">
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((emp) => (
                          <div
                            key={emp}
                            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, name: emp }));
                              setDropdownOpen(null);
                            }}
                          >
                            <div
                              className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                                formData.name === emp
                                  ? "bg-blue-500 text-white"
                                  : "border-gray-300"
                              }`}
                            >
                              {formData.name === emp && (
                                <Check className="h-4 w-4" />
                              )}
                            </div>
                            <span>{emp}</span>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-gray-500 text-center">
                          No employees found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Department
            </label>
            <Select
              value={formData.department}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, department: value }))
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {PETTY_CASH_DEPARTMENTS.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Details
            </label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Enter transaction details"
              className="w-full h-20 resize-none"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : formData.transactionType === "deposit" ? (
                "Add Deposit"
              ) : (
                "Add Withdrawal"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
